import React, { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Scroll, ChevronDown, Menu, X } from 'lucide-react';
import ShareButton from './ShareButton';
import Footer from './Footer';

const CALCULATORS = [
    ['/calculators/birthverse', 'BirthVerse Calculator'],
    ['/calculators/life-path', 'Life Path Number'],
    ['/calculators/moon-phase', 'Moon Phase Calculator'],
    ['/calculators/birth-chart', 'Birth Chart'],
    ['/calculators/chinese-zodiac', 'Chinese Zodiac'],
];

const Layout: React.FC = () => {
    const [isCalculatorsOpen, setIsCalculatorsOpen] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const calculators = useRef<HTMLDivElement>(null);
    const calculatorButton = useRef<HTMLButtonElement>(null);
    const mobileButton = useRef<HTMLButtonElement>(null);
    const header = useRef<HTMLElement>(null);
    const main = useRef<HTMLElement>(null);
    const path = useLocation().pathname.replace(/\/+$/, '') || '/';
    const previousPath = useRef(path);

    useEffect(() => {
        if (previousPath.current === path) return;
        previousPath.current = path;
        const previousBehavior = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, 0);
        document.documentElement.style.scrollBehavior = previousBehavior;
        main.current?.focus({ preventScroll: true });
    }, [path]);

    useEffect(() => {
        const closeOutside = (event: PointerEvent) => {
            if (!calculators.current?.contains(event.target as Node)) setIsCalculatorsOpen(false);
            if (!header.current?.contains(event.target as Node)) setIsMobileOpen(false);
        };
        document.addEventListener('pointerdown', closeOutside);
        return () => document.removeEventListener('pointerdown', closeOutside);
    }, []);

    const closeNavigation = () => {
        setIsCalculatorsOpen(false);
        setIsMobileOpen(false);
    };
    const links = CALCULATORS.map(([path, label]) => (
        <Link key={path} to={path} onClick={closeNavigation} className="block px-4 py-3 text-gray-700 hover:bg-[#F5F3FF] hover:text-[#4F46E5] rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500">
            {label}
        </Link>
    ));

    return (
        <div className="min-h-screen bg-[#F8F7FF] flex flex-col">
            <header ref={header} className="bg-white shadow-sm" onKeyDown={event => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    if (isCalculatorsOpen) calculatorButton.current?.focus();
                    if (isMobileOpen) mobileButton.current?.focus();
                    closeNavigation();
                }
            }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center gap-3">
                        <Link to="/" onClick={closeNavigation} className="flex items-center gap-2 hover:opacity-80" aria-label="Return to homepage">
                            <Scroll className="h-6 w-6 text-[#4F46E5]" aria-hidden="true" />
                            <span className="text-xl font-bold text-gray-900">BirthVerse</span>
                        </Link>
                        <div className="flex items-center gap-4 sm:gap-6">
                            <nav aria-label="Main navigation" className="hidden md:flex items-center gap-6">
                                <div ref={calculators} className="relative" onBlur={event => {
                                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsCalculatorsOpen(false);
                                }}>
                                    <button ref={calculatorButton} type="button" onClick={() => setIsCalculatorsOpen(open => !open)} aria-expanded={isCalculatorsOpen} aria-controls="calculator-links" className="flex items-center gap-1 text-gray-600 hover:text-[#4F46E5] font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 rounded">
                                        Calculators<ChevronDown className={`h-4 w-4 ${isCalculatorsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                                    </button>
                                    {isCalculatorsOpen && <div id="calculator-links" className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">{links}</div>}
                                </div>
                                <Link to="/blog" onClick={closeNavigation} className="text-gray-600 hover:text-[#4F46E5] font-medium">Blog</Link>
                            </nav>
                            <div className="hidden sm:block"><ShareButton /></div>
                            <button ref={mobileButton} type="button" onClick={() => setIsMobileOpen(open => !open)} aria-label={isMobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={isMobileOpen} aria-controls="mobile-navigation" className="md:hidden inline-flex items-center gap-2 p-2 rounded-lg text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500">
                                {isMobileOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}<span>Menu</span>
                            </button>
                        </div>
                    </div>
                    {isMobileOpen && <nav id="mobile-navigation" aria-label="Mobile navigation" className="md:hidden mt-4 border-t border-gray-100 pt-3">
                        {links}
                        <Link to="/blog" onClick={closeNavigation} className="block px-4 py-3 text-gray-700 hover:bg-[#F5F3FF] rounded-lg">Blog</Link>
                    </nav>}
                </div>
            </header>
            <main ref={main} tabIndex={-1} className="flex-grow focus:outline-none"><Outlet /></main>
            <Footer />
        </div>
    );
};
export default Layout;
